import React, { useEffect, useMemo, useState } from "react";
import {
    Championship,
    Collections,
    Race,
    Track,
    useCollection,
    useRounds,
    useThemeBreakpoints,
} from "api";
import { Box, Grid, Tab, Tabs } from "@material-ui/core";
import { DateTime } from "luxon";
import groupBy from "lodash/groupBy";
import { RaceStandings } from "ui/views/ChampionshipStandings/RaceStandings";
import { ChampionshipDriver } from "api/model/Championship/ChampionshipDriver";

interface RoundStandingsProps {
    championship: Championship;
    drivers: ChampionshipDriver[];
    selectedClass?: string;
}

const selectRoundTab = (setSelectedRound) => (event, value) => {
    setSelectedRound(value);
};

const RoundStandings: React.FC<RoundStandingsProps> = ({
    championship,
    drivers,
    selectedClass,
}) => {
    const { isDownMd } = useThemeBreakpoints();
    const [selectedRound, setSelectedRound] = useState("-1");
    const { id: championshipId = "-1" } = championship;

    const rounds = useRounds({ championshipId });
    const tracksById =
        useCollection<{ [key: string]: Track }>({
            collection: Collections.TRACKS,
        }) || {};
    const races = useCollection<Race[]>(
        {
            collection: Collections.RACES,
            where: ["championshipId", "==", championshipId],
            storeAs: `races_${championshipId}`,
        },
        true
    );

    const racesByRoundId = useMemo(
        () => groupBy(races, (race) => race.roundId),
        [races]
    );
    const selectedRaces = useMemo(() => {
        if (selectedRound && racesByRoundId[selectedRound]) {
            return racesByRoundId[selectedRound].sort(
                (race1, race2) => race1.raceNumber - race2.raceNumber
            );
        }
        return [];
    }, [racesByRoundId, selectedRound]);

    useEffect(() => {
        const roundIds = rounds.map((round) => round.id);
        const today = DateTime.local().toFormat("yyyy-MM-dd");

        if (!selectedRound || !roundIds.includes(selectedRound)) {
            const roundsBeforeEqualToday = rounds
                .filter((round) => round.date <= today)
                .sort(
                    (r1, r2) =>
                        DateTime.fromISO(r2.date).toMillis() -
                        DateTime.fromISO(r1.date).toMillis()
                );
            if (roundsBeforeEqualToday.length > 0) {
                setSelectedRound(roundsBeforeEqualToday[0].id || "");
            } else {
                setSelectedRound(rounds[0] ? rounds[0].id || "" : "");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rounds]);

    if (!rounds || rounds.length === 0) {
        return null;
    }

    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Box>
                    <Tabs
                        variant={isDownMd ? "scrollable" : "fullWidth"}
                        centered={!isDownMd}
                        indicatorColor="primary"
                        textColor="primary"
                        scrollButtons="on"
                        value={selectedRound}
                        onChange={selectRoundTab(setSelectedRound)}
                    >
                        {rounds.map(({ id, date, trackId }) => {
                            const tabName = `${
                                tracksById[trackId]?.friendlyName
                            } - ${DateTime.fromFormat(
                                date,
                                "yyyy-MM-dd"
                            ).toFormat("dd/MM/yyyy")}`;
                            return (
                                <Tab
                                    key={`round_${id}`}
                                    label={tabName}
                                    value={id}
                                    style={{ fontSize: "0.85rem" }}
                                />
                            );
                        })}
                    </Tabs>
                </Box>
            </Grid>
            {selectedRaces.map((race) => (
                <Grid item xs={12} key={race.raceNumber}>
                    <RaceStandings
                        race={race}
                        showTitle={selectedRaces.length > 1}
                        championship={championship}
                        drivers={drivers}
                        selectedClass={selectedClass}
                    />
                </Grid>
            ))}
        </Grid>
    );
};

export { RoundStandings };
